import _extends from "@babel/runtime/helpers/extends";
import { Button, Checkbox, createStyles, makeStyles, TableCell, TableSortLabel, Tooltip } from '@material-ui/core';
import { Build } from '@material-ui/icons';
import clsx from 'clsx';
import React, { useCallback, useMemo } from 'react';
import { AutoSizer, Column, Table } from 'react-virtualized';
import { ActionsCell, DateCell, ReferenceCell, RowCheckbox, VirtualDefaultCell, VirtualDisplayNameCell } from "./index.js";
const useStyles = makeStyles(theme => createStyles({
  root: {
    '& .ReactVirtualized__Table__headerRow': {
      backgroundColor: theme.palette.type === 'dark' ? 'hsl(0deg 0% 24%)' : 'hsl(0deg 0% 92%)',
      boxShadow: theme.palette.type === 'dark' ? '0px 3px 2px hsl(0deg 0% 0% / 30%)' : '1px 1px 3px 0px hsl(0deg 0% 0% / 28%)'
    },
    '& .ReactVirtualized__Table__row': {}
  },
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box'
  },
  tableRow: {
    cursor: 'pointer'
  },
  tableRowHover: {
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? 'hsl(0deg 0% 51% / 10%)' : 'hsl(0deg 0% 0% / 3.5%)'
    }
  },
  tableCell: {
    flex: 1
  },
  noClick: {
    cursor: 'initial'
  },
  selected: {
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
  },
  label: {
    display: 'flex',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    letterSpacing: '1px',
    color: theme.palette.type === 'dark' ? 'hsl(0deg 0% 60%)' : 'hsl(0deg 0% 40%)'
  }
}));
const minColumnWith = field => {
  if (field !== 'Checkbox' && field !== 'ColumnSettings') {
    return;
  }
  return 48;
};
const maxColumnWith = field => {
  if (field !== 'Checkbox' && field !== 'ColumnSettings') {
    return;
  }
  return 48;
};
const EmptyCell = () => {
  return /*#__PURE__*/React.createElement(TableCell, {
    style: {
      height: '57px',
      opacity: '0',
      width: '50px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 0,
      maxWidth: '48px'
    },
    component: "div",
    key: "EmptyField"
  });
};
export const VirtualizedTable = props => {
  const classes = useStyles();
  const handleSelectAllClick = () => {
    props.onRequestSelectionChange && (props.selected && props.selected.length === props.items.length ? props.onRequestSelectionChange([]) : props.onRequestSelectionChange(props.items));
  };
  const handleContentSelection = content => {
    const tempSelected = props.selected !== undefined && props.selected.length > 0 ? props.selected : [];
    if (props.onRequestSelectionChange) {
      if (tempSelected.find(c => c.Id === content.Id)) {
        props.onRequestSelectionChange(tempSelected.filter(s => s.Id !== content.Id));
      } else {
        props.onRequestSelectionChange([...tempSelected, content]);
      }
    }
  };
  const getRowClassName = ({
    index
  }) => {
    const {
      tableProps: {
        onRowClick
      }
    } = props;
    const isActive = index !== -1 && props.active && props.active.Id === props.items[index].Id ? true : false;
    return clsx(classes.tableRow, classes.flexContainer, {
      [classes.tableRowHover]: index !== -1 && onRowClick != null
    }, {
      [classes.selected]: isActive
    });
  };
  const checkBoxRenderer = (tableCellProps, isSelected) => {
    return /*#__PURE__*/React.createElement(RowCheckbox, {
      getSelectionControl: props.getSelectionControl,
      isSelected: isSelected,
      rowData: tableCellProps.rowData,
      checkboxProps: props.checkboxProps,
      handleContentSelection: handleContentSelection
    });
  };
  const defaultCellRenderer = ({
    cellData,
    dataKey,
    rowData
  }) => {
    switch (dataKey) {
      case 'DisplayName':
        return /*#__PURE__*/React.createElement(VirtualDisplayNameCell, {
          rowData: rowData,
          icons: props.icons
        });
      case 'Actions':
        if (rowData.Actions && rowData.Actions instanceof Array) {
          return /*#__PURE__*/React.createElement(ActionsCell, {
            actions: rowData.Actions,
            content: rowData,
            openActionMenu: ev => props.onRequestActionsMenu && props.onRequestActionsMenu(ev, rowData),
            virtual: true
          });
        }
        break;
      case 'ModificationDate':
        return /*#__PURE__*/React.createElement(DateCell, {
          date: cellData,
          virtual: true
        });
      default:
        break;
    }
    const field = rowData[dataKey];
    if (field && field.Id && field.Path && field.DisplayName) {
      return /*#__PURE__*/React.createElement(ReferenceCell, {
        content: field,
        fieldName: 'DisplayName',
        virtual: true
      });
    }
    return /*#__PURE__*/React.createElement(VirtualDefaultCell, {
      cellData: cellData
    });
  };
  const fieldSchemas = useMemo(() => props.schema.FieldSettings.reduce((v, field) => {
    ;
    v[field.Name] = props.schema.FieldSettings.find(s => s.Name === field.Name);
    return v;
  }, {}), [props.schema]);
  const getSchemaForField = useCallback(fieldName => fieldSchemas[fieldName], [fieldSchemas]);
  const headerRenderer = (columnSetting, columnCount, autoSizerWidth) => {
    var _columnSetting$field;
    const {
      tableProps: {
        headerHeight
      }
    } = props;
    if (columnSetting.field === 'ColumnSettings') {
      return /*#__PURE__*/React.createElement(TableCell, {
        key: "ColumnSettings",
        style: {
          width: '48px',
          height: '42px',
          maxWidth: '48px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        },
        component: "div"
      }, /*#__PURE__*/React.createElement(Tooltip, {
        title: "Column Settings",
        placement: "bottom"
      }, /*#__PURE__*/React.createElement(Button, {
        "data-test": "column-settings",
        onClick: props.handleColumnSettingsClick
      }, /*#__PURE__*/React.createElement(Build, null))));
    }
    if (columnSetting.field === 'Checkbox') {
      return /*#__PURE__*/React.createElement(TableCell, {
        padding: "checkbox",
        key: "selectAll",
        style: {
          width: '48px',
          height: '42px',
          paddingRight: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        },
        component: "div"
      }, /*#__PURE__*/React.createElement(Checkbox, _extends({
        className: "select-all",
        indeterminate: props.selected && props.selected.length > 0 && props.selected.length !== props.items.length ? true : false,
        checked: props.selected && props.selected.length === props.items.length ? true : false,
        onChange: handleSelectAllClick
      }, props.checkboxProps)));
    }
    const fieldSetting = getSchemaForField(columnSetting.field);
    const displayName = columnSetting.title || fieldSetting && fieldSetting.DisplayName || columnSetting.field;
    const description = fieldSetting && fieldSetting.Description || columnSetting.field;
    return /*#__PURE__*/React.createElement(TableCell, {
      "data-test": `table-header-${(_columnSetting$field = columnSetting.field) === null || _columnSetting$field === void 0 ? void 0 : _columnSetting$field.replace(/\s+/g, '-').toLowerCase()}`,
      component: "div",
      variant: "head",
      style: {
        height: headerHeight || 42,
        width: props.displayRowCheckbox ? 'auto' : autoSizerWidth / columnCount,
        display: 'flex',
        padding: columnSetting.field === 'Actions' ? '0 0 0 0px' : 0,
        alignItems: 'center',
        justifyContent: columnSetting.field === 'Actions' ? 'center' : 'left'
      },
      className: columnSetting.field
    }, /*#__PURE__*/React.createElement(Tooltip, {
      title: description
    }, /*#__PURE__*/React.createElement(TableSortLabel, {
      className: classes.label,
      active: props.orderBy === columnSetting.field,
      direction: props.orderDirection,
      onClick: () => props.onRequestOrderChange && props.onRequestOrderChange(columnSetting.field, props.orderDirection === 'asc' ? 'desc' : 'asc')
    }, displayName)));
  };
  const {
    fieldsToDisplay,
    tableProps
  } = props;
  const currentFieldsToDisplay = [...fieldsToDisplay];

  /*An object which will contain the extend field*/

  const aditionalFields = {
    Checkbox: {
      field: 'Checkbox',
      title: 'Checkbox'
    },
    ColumnSettins: {
      field: 'ColumnSettings',
      title: 'ColumnSettings'
    }
  };

  /*An array which will contain the extend field*/

  if (props.displayRowCheckbox) {
    currentFieldsToDisplay.unshift(aditionalFields.Checkbox);
  }
  if (!currentFieldsToDisplay.find(f => f.field === 'Actions')) {
    currentFieldsToDisplay.push({
      field: 'Actions',
      title: 'Actions'
    });
  }
  if (!props.disableColumnSettings) {
    currentFieldsToDisplay.push(aditionalFields.ColumnSettins);
  }
  return /*#__PURE__*/React.createElement(AutoSizer, null, ({
    height,
    width
  }) => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Table, _extends({
    className: classes.root,
    height: height,
    overscanRowCount: 10000,
    width: width,
    gridStyle: {
      direction: 'inherit',
      outline: 'none'
    }
  }, tableProps, {
    rowClassName: getRowClassName
  }), currentFieldsToDisplay.map(field => {
    const currentField = field.field;
    return /*#__PURE__*/React.createElement(Column, {
      flexGrow: props.displayRowCheckbox && field.field === 'Checkbox' ? 0 : 0,
      flexShrink: props.displayRowCheckbox && field.field === 'Checkbox' ? 5 : 1,
      key: currentField,
      columnData: {
        label: currentField
      },
      headerRenderer: () => headerRenderer(field, currentFieldsToDisplay.length, width),
      className: classes.flexContainer,
      cellRenderer: tableCellProps => {
        if (currentField === 'ColumnSettings') {
          EmptyCell();
          return;
        }
        if (props.displayRowCheckbox && currentField === 'Checkbox') {
          const isSelected = props.selected && props.selected.find(s => s.Id === tableCellProps.rowData.Id) ? true : false;
          return checkBoxRenderer(tableCellProps, isSelected);
        } else {
          if (tableCellProps.dataKey.includes('/')) {
            return props.referenceCellRenderer ? props.referenceCellRenderer(tableCellProps) : defaultCellRenderer(tableCellProps);
          } else {
            return props.cellRenderer ? props.cellRenderer({
              tableCellProps,
              fieldSettings: getSchemaForField(tableCellProps.dataKey)
            }) : defaultCellRenderer(tableCellProps);
          }
        }
      },
      dataKey: currentField,
      width: width,
      minWidth: minColumnWith(currentField),
      maxWidth: maxColumnWith(currentField)
    });
  }))));
};
//# sourceMappingURL=virtualized-table.js.map